// Test & Refine redesign - History list.
// Rows come from the real simulationHistory; clicking a row loads that run
// into Results via the orchestrator's fetchSandboxRun handler.
import { TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import type { SimulationHistoryItem } from "@/app/services/simulation/ai-sandbox-service";
import { signedPp } from "./shared";

export function HistoryView({
  history,
  onSelectRun,
}: {
  history: SimulationHistoryItem[];
  onSelectRun: (runId: number) => void;
}) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)" }}>Simulation history</div>
          <div style={{ fontSize: 12.5, color: "var(--muted-foreground)" }}>
            Re-open any run to compare before and after deltas over time.
          </div>
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--muted-foreground)",
            background: "var(--muted)",
            border: "1px solid var(--border)",
            borderRadius: 9999,
            padding: "4px 11px",
          }}
        >
          {history.length} {history.length === 1 ? "run" : "runs"}
        </span>
      </div>

      {history.length === 0 ? (
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: "40px 24px",
            textAlign: "center",
            background: "var(--muted)",
            fontSize: 13.5,
            color: "var(--muted-foreground)",
          }}
        >
          No simulations run yet. Configure a test and run it to build history here.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {history.map((h) => (
            <button
              key={h.id}
              onClick={() => onSelectRun(h.id)}
              className="tr-row"
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 16,
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "16px 18px",
                background: "var(--card)",
                cursor: "pointer",
                boxShadow: "var(--elevation-sm)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--foreground)", lineHeight: 1.35, marginBottom: 4 }}>
                  "{h.query}"
                </div>
                <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                  {formatTimestamp(h.timestamp)} · {h.models.length} models tested
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 14,
                    fontWeight: 700,
                    color: h.avgDelta < 0 ? "var(--infrared-coral)" : "var(--signal-green)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {h.avgDelta < 0 ? <TrendingDown size={13} strokeWidth={2.2} /> : <TrendingUp size={13} strokeWidth={2.2} />}
                  {signedPp(h.avgDelta)}
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--teal-text)", marginTop: 3, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  Load results
                  <ArrowRight size={12} strokeWidth={2} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTimestamp(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
